import { useEffect, useRef, useState, useCallback } from 'react';
import { peerConnection } from '../../services/peerConnection';
import { useRdpStore, type MonitorInfo } from '../../stores/rdpStore';
import { useMachineStore } from '../../stores/machineStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { isWeb } from '../../lib/platform';
import { logger } from '@/lib/logger';
import RdpToolbar from './RdpToolbar';
import RdpInputOverlay from './RdpInputOverlay';
import { Monitor, MonitorOff, Loader2 } from 'lucide-react';

const TAG = 'RdpViewer';

type ScaleMode = 'fit' | 'fill' | 'native';

export default function RdpViewer() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isViewing,
    remoteStream,
    isFullscreen,
    inputEnabled,
    stats,
    setViewing,
    setRemoteStream,
    setRemoteScreenDimensions,
    setAvailableMonitors,
    setFullscreen,
    setInputEnabled,
    updateStats,
    reset,
  } = useRdpStore();

  const selectedMachineId = useMachineStore((s) => s.selectedMachineId);
  const machineName = useSettingsStore((s) => s.server.machineName);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scaleMode, setScaleMode] = useState<ScaleMode>('fit');
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevTotalFramesRef = useRef(0);
  const prevTimestampRef = useRef(0);

  const webMode = isWeb();

  // Subscribe to remote video tracks
  useEffect(() => {
    const unsub = peerConnection.onRemoteTrack((stream) => {
      logger.info(TAG, 'Received remote video stream');
      setRemoteStream(stream);
    });

    // Listen for rdp_stopped and clipboard events from host
    const unsubEvent = peerConnection.onEvent((event, payload) => {
      if (event === 'rdp_stopped') {
        logger.info(TAG, 'Host stopped RDP');
        wasViewingRef.current = false; // Prevent auto-restore from restarting
        setViewing(false);
        setRemoteStream(null);
      } else if (event === 'rdp_clipboard_changed') {
        // Host clipboard changed — write to local clipboard
        const content = (payload as { content?: string })?.content;
        if (content) {
          navigator.clipboard.writeText(content).catch((err) => {
            logger.warn(TAG, 'Failed to write remote clipboard to local:', err);
          });
        }
      }
    });

    return () => {
      unsub();
      unsubEvent();
    };
  }, [setRemoteStream, setViewing]);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
    }
    if (videoRef.current && !remoteStream) {
      videoRef.current.srcObject = null;
    }
  }, [remoteStream]);

  // Fullscreen management
  useEffect(() => {
    const handleFsChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, [setFullscreen]);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await containerRef.current.requestFullscreen();
    }
  }, []);

  // Start RDP session
  const startRdp = useCallback(async () => {
    if (!webMode || !selectedMachineId) return;
    setConnecting(true);
    setError(null);

    try {
      const result = await peerConnection.sendCommand<{
        screenWidth: number;
        screenHeight: number;
        scaleFactor: number;
        monitors: MonitorInfo[];
        inputEnabled?: boolean;
      }>('rdp_start', { requesterName: machineName || 'Web Client' });

      setRemoteScreenDimensions(result.screenWidth, result.screenHeight, result.scaleFactor);
      setAvailableMonitors(result.monitors);
      setViewing(true, selectedMachineId);

      // Respect host's input forwarding decision
      if (result.inputEnabled === false) {
        setInputEnabled(false);
      }

      logger.info(TAG, `RDP session started: ${result.screenWidth}x${result.screenHeight}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start RDP';
      setError(msg);
      logger.error(TAG, 'Failed to start RDP:', err);
    } finally {
      setConnecting(false);
    }
  }, [webMode, selectedMachineId, machineName, setRemoteScreenDimensions, setAvailableMonitors, setViewing, setInputEnabled]);

  // Stop RDP session
  const stopRdp = useCallback(async () => {
    wasViewingRef.current = false; // Prevent auto-restore from restarting
    try {
      await peerConnection.sendCommand('rdp_stop', {});
    } catch (err) {
      logger.warn(TAG, 'Error sending rdp_stop:', err);
    }
    reset();
  }, [reset]);

  // Auto-restore RDP session on WebRTC reconnect
  const webrtcReady = useMachineStore((s) => s.webrtcReady);
  const wasViewingRef = useRef(false);

  useEffect(() => {
    if (isViewing) {
      wasViewingRef.current = true;
    }
  }, [isViewing]);

  useEffect(() => {
    if (webrtcReady && wasViewingRef.current && !isViewing && !connecting && selectedMachineId) {
      logger.info(TAG, 'WebRTC reconnected, restoring RDP session');
      wasViewingRef.current = false;
      startRdp();
    }
  }, [webrtcReady, isViewing, connecting, selectedMachineId, startRdp]);

  // Poll stats every 2s while viewing
  useEffect(() => {
    if (!isViewing) {
      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
      prevTotalFramesRef.current = 0;
      prevTimestampRef.current = 0;
      return;
    }

    statsTimerRef.current = setInterval(async () => {
      // FPS from video playback quality
      if (videoRef.current) {
        const video = videoRef.current;
        const quality = (video as HTMLVideoElement & { getVideoPlaybackQuality?: () => { totalVideoFrames: number } })
          .getVideoPlaybackQuality?.();
        if (quality) {
          const now = performance.now();
          if (prevTimestampRef.current > 0) {
            const deltaFrames = quality.totalVideoFrames - prevTotalFramesRef.current;
            const deltaTime = (now - prevTimestampRef.current) / 1000;
            if (deltaTime > 0) {
              updateStats({ fps: Math.round(deltaFrames / deltaTime) });
            }
          }
          prevTotalFramesRef.current = quality.totalVideoFrames;
          prevTimestampRef.current = now;
        }
      }

      // Bitrate and latency from RTCPeerConnection.getStats()
      const rtcStats = await peerConnection.getRtcStats();
      if (rtcStats) {
        updateStats({ bitrate: rtcStats.bitrate, latency: rtcStats.latency });
      }
    }, 2000);

    return () => {
      if (statsTimerRef.current) clearInterval(statsTimerRef.current);
    };
  }, [isViewing, updateStats]);

  // Compute video style based on scale mode
  const getVideoStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      cursor: inputEnabled ? 'none' : 'default',
    };
    switch (scaleMode) {
      case 'fill':
        return { ...base, width: '100%', height: '100%', objectFit: 'cover' as const };
      case 'native':
        return { ...base, objectFit: 'none' as const };
      case 'fit':
      default:
        return { ...base, maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' as const };
    }
  };

  // Not in web mode or no machine selected
  if (!webMode) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Monitor className="w-12 h-12 text-base-content/20 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Remote Desktop</h3>
        <p className="text-sm text-base-content/60 max-w-sm">
          Remote Desktop is available from the web client. Connect to a desktop machine to start viewing.
        </p>
      </div>
    );
  }

  if (!peerConnection.isClient) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <MonitorOff className="w-12 h-12 text-base-content/20 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Not Connected</h3>
        <p className="text-sm text-base-content/60 max-w-sm">
          Connect to a desktop machine first using the Machine Selector, then start a remote desktop session.
        </p>
      </div>
    );
  }

  // Connected but not viewing — show start button
  if (!isViewing) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Monitor className="w-16 h-16 text-primary/40 mb-6" />
        <h3 className="text-lg font-semibold mb-2">Remote Desktop</h3>
        <p className="text-sm text-base-content/60 max-w-sm mb-6">
          View and control the remote machine's desktop. The host will be prompted to share their screen.
        </p>

        {error && (
          <div className="alert alert-error mb-4 max-w-sm text-sm">
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={startRdp}
          disabled={connecting}
          className="btn btn-primary gap-2"
        >
          {connecting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Monitor className="w-5 h-5" />
              Start Remote Desktop
            </>
          )}
        </button>
      </div>
    );
  }

  // Active RDP session
  return (
    <div ref={containerRef} className="relative flex flex-col h-full bg-black overflow-hidden">
      {/* Toolbar */}
      <RdpToolbar
        onToggleFullscreen={toggleFullscreen}
        onDisconnect={stopRdp}
        onScaleModeChange={setScaleMode}
        isFullscreen={isFullscreen}
        stats={stats}
        scaleMode={scaleMode}
      />

      {/* Video + Input overlay container */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={getVideoStyle()}
        />

        {/* Input overlay (captures mouse/keyboard when enabled) */}
        {inputEnabled && remoteStream && (
          <RdpInputOverlay videoRef={videoRef} />
        )}

        {/* Loading indicator when waiting for stream */}
        {!remoteStream && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-white/60">Waiting for host screen...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
